import type { Sessao } from '@/auth/session'
import { CLIENTES_STUB } from '@/tenant/clientesStub'

/**
 * The three personas of the handoff, as sessions.
 *
 * These are load-bearing for stories 0.4 and 0.5, not demo data:
 *
 *  - **Administrador** reaches `/admin/*` and every client — the only persona that
 *    can exercise the client selector's switch behaviour (BR-19).
 *  - **Arquiteto Cloud** holds a STRICT SUBSET of the clients. It exists to prove the
 *    selector lists `{clientes}` and not every tenant (§5.2).
 *  - **Cliente** holds EXACTLY ONE client. §5.2 requires the selector to render
 *    anyway, disabled, without shifting the layout — a case that only shows up if a
 *    fixture produces it.
 *
 * ⚠️ With only two tenants in `CLIENTES_STUB` (both drawn in shot `2n`; inventing a
 * third would be guesswork), "strict subset" and "exactly one" collapse into the
 * same list length for the Arquiteto. Story 0.5 separates them when the real
 * `{clientes}` list arrives from the API.
 */

const [TRIBUNAL, ORGAO_B] = CLIENTES_STUB.map((cliente) => cliente.idCliente) as [string, string]

/**
 * Mock mode only. There is no secret here and none is implied: mock mode is refused
 * outright in a production build (see `escolherAdaptador`), and this string never
 * reaches a Cognito pool.
 */
export const SENHA_MOCK = 'iox-demo'

/**
 * The temporary password an Administrador hands out with an invitation. Signing in
 * with it opens the `FORCE_CHANGE_PASSWORD` challenge instead of a session, exactly
 * as the real pool does.
 */
export const SENHA_TEMPORARIA_MOCK = 'iox-convite'

/**
 * The code the mock accepts in place of the one Cognito would e-mail.
 *
 * Six digits because that is what Cognito sends — a mock that accepted `abc` would
 * let a field-length or numeric-input mistake through unnoticed.
 */
export const CODIGO_REDEFINICAO_MOCK = '123456'

/**
 * A reserved code that always comes back expired.
 *
 * An expired code is the one reset failure a user actually hits — codes sit unread in
 * an inbox — and it is the only one whose copy must send them BACK a step instead of
 * telling them to look again. Without a way to trigger it, that path would first run
 * on the real pool.
 */
export const CODIGO_EXPIRADO_MOCK = '000000'

export interface FixtureDeSessao extends Sessao {
  /** Shown in the dev-only hint on `/login`. */
  rotulo: string
  /**
   * `true` for a persona that has never signed in — first login must go through
   * "defina sua senha". A STATE, not a fourth persona: this fixture's grupos and
   * clientes are the Cliente's.
   */
  primeiroAcesso?: boolean
}

export const FIXTURES_DE_SESSAO: FixtureDeSessao[] = [
  {
    rotulo: 'Administrador',
    sub: 'mock-administrador',
    nome: 'Ana Administradora',
    email: 'admin@ioxcloud.com.br',
    grupos: ['administrador'],
    clientes: [TRIBUNAL, ORGAO_B],
  },
  {
    rotulo: 'Arquiteto Cloud',
    sub: 'mock-arquiteto',
    nome: 'Artur Arquiteto',
    email: 'arquiteto@ioxcloud.com.br',
    grupos: ['arquiteto-iox'],
    clientes: [TRIBUNAL],
  },
  {
    rotulo: 'Cliente',
    sub: 'mock-cliente',
    nome: 'Clara Cliente',
    email: 'clara@orgao.jus.br',
    grupos: ['cliente'],
    clientes: [ORGAO_B],
  },
  {
    // Exists so the first-login flow is demoable before the pool does. Same grupos
    // and clientes as the Cliente — the difference is the ACCOUNT STATE, not the
    // persona.
    rotulo: 'Convidado (primeiro acesso)',
    sub: 'mock-convidado',
    nome: 'Caio Convidado',
    email: 'convidado@orgao.jus.br',
    grupos: ['cliente'],
    clientes: [ORGAO_B],
    primeiroAcesso: true,
  },
]

export function fixturePorEmail(email: string): FixtureDeSessao | undefined {
  const alvo = email.trim().toLowerCase()
  return FIXTURES_DE_SESSAO.find((fixture) => fixture.email.toLowerCase() === alvo)
}

/** Drops the fixture-only fields — screens must not see what a real session lacks. */
export function comoSessao({ sub, nome, email, grupos, clientes }: FixtureDeSessao): Sessao {
  return { sub, nome, email, grupos, clientes }
}
